import React, { useState, useEffect, useRef, useCallback } from "react";
import { ImageAnalysis, imageAnalysisService } from "../../services/imageAnalysisService";
import { AlertCircle, Camera, FileText, ClipboardList, Activity, Send, HelpCircle, X, Pencil, MousePointer } from "lucide-react";
import { format } from "date-fns";
import axios from "../../config/axios";

interface ImageAnalysisDetailProps {
  analysisId: string | null;
}

interface ChatMessage {
  type: 'user' | 'system';
  content: string;
  time: Date;
}

interface QAResponse {
  answer: string;
  confidence: string;
  references: string[];
  additional_information: string;
  findings: {
    description: string;
    location: string;
    coordinates: {
      x: number;
      y: number;
      width: number;
      height: number;
    };
    severity: string;
    diagnostic_confidence: string;
  }[];
}

// Simple markdown parser to handle basic formatting
const parseMarkdown = (text: string): string => {
  if (!text) return '';
  
  // Bold: **text** or __text__
  let formattedText = text.replace(/\*\*(.*?)\*\*|__(.*?)__/g, (_, group1, group2) => 
    `<strong>${group1 || group2}</strong>`
  );
  
  // Italic: *text* or _text_
  formattedText = formattedText.replace(/\*(.*?)\*|_(.*?)_/g, (_, group1, group2) => 
    `<em>${group1 || group2}</em>`
  );
  
  // Line breaks
  formattedText = formattedText.replace(/\n/g, '<br />');
  
  // Headers
  formattedText = formattedText.replace(/### (.*?)(?:\n|$)/g, '<h3 class="text-lg font-semibold mt-2 mb-1">$1</h3>');
  formattedText = formattedText.replace(/## (.*?)(?:\n|$)/g, '<h2 class="text-xl font-semibold mt-3 mb-2">$1</h2>');
  formattedText = formattedText.replace(/# (.*?)(?:\n|$)/g, '<h1 class="text-2xl font-bold mt-4 mb-2">$1</h1>');
  
  // Lists
  formattedText = formattedText.replace(/^\s*[-*+]\s+(.*?)(?:\n|$)/gm, '<li class="ml-4">$1</li>');
  formattedText = formattedText.replace(/(<li.*?>.*?<\/li>)(?:\n|$)+(<li.*?>)/g, '$1$2');
  formattedText = formattedText.replace(/(<li.*?>.*?<\/li>)(?:\n|$)+/g, '<ul class="list-disc pl-5 my-2">$1</ul>');
  
  return formattedText;
};

export const ImageAnalysisDetail: React.FC<ImageAnalysisDetailProps> = ({ analysisId }) => {
  const [analysis, setAnalysis] = useState<ImageAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showChat, setShowChat] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([{
    type: 'system',
    content: 'Ask questions about this image to get detailed analysis.',
    time: new Date()
  }]);
  const [inputValue, setInputValue] = useState('');
  const [isAnswering, setIsAnswering] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatSidebarRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  
  // Add a state to track whether we're in Q&A mode (showing chat UI)
  const [isQAMode, setIsQAMode] = useState(false);
  
  const formatDate = (dateString: string) => {
    if (!dateString) return "Date not available";
    
    try {
      const date = new Date(dateString);
      return format(date, "MMMM d, yyyy 'at' h:mm a");
    } catch (error) {
      return "Invalid date";
    }
  };

  const formatSeverity = (severity: string) => {
    if (!severity) return "";
    
    const severityLower = severity.toLowerCase();
    if (severityLower === "mild") return "text-yellow-600 bg-yellow-100";
    if (severityLower === "moderate") return "text-orange-600 bg-orange-100";
    if (severityLower === "severe") return "text-red-600 bg-red-100";
    if (severityLower === "normal") return "text-green-600 bg-green-100";
    
    return "text-gray-600 bg-gray-100";
  };

  useEffect(() => {
    const fetchAnalysis = async () => {
      if (!analysisId) {
        setAnalysis(null);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        // Pass the analysisId directly without conversion
        const data = await imageAnalysisService.getImageAnalysisByRecordingId(analysisId);
        setAnalysis(data);
      } catch (err) {
        console.error("Error fetching image analysis:", err);
        setError("Failed to load image analysis. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalysis();
  }, [analysisId]);

  // Reset chat when a different recording is selected
  useEffect(() => {
    // Reset chat to initial state when analysisId changes
    setMessages([{
      type: 'system',
      content: 'Ask questions about this image to get detailed analysis.',
      time: new Date()
    }]);
    setInputValue('');
    setIsAnswering(false);
    setShowChat(false);
    setIsQAMode(false); // Reset to standard view when image changes
  }, [analysisId]);

  // Auto-scroll to the latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle click outside of chat sidebar
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showChat && 
          chatSidebarRef.current && 
          !chatSidebarRef.current.contains(event.target as Node)) {
        setShowChat(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showChat]);

  // Handle escape key to close sidebar
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showChat) {
        setShowChat(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showChat]);

  const enterQAMode = () => {
    setIsQAMode(true);
    setShowChat(true);
  };

  const exitQAMode = () => {
    setIsQAMode(false);
    setShowChat(false);
  };

  const toggleChat = () => {
    // When in Q&A mode, simply toggle the chat panel visibility
    setShowChat(prev => !prev);
    
    // Only enter Q&A mode if we're showing the chat and not already in Q&A mode
    if (!isQAMode && !showChat) {
      setIsQAMode(true);
    }
  };

  const handleAskQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inputValue.trim() || !analysisId) return;

    const question = inputValue.trim();
    
    // Add user message
    setMessages(prev => [...prev, {
      type: 'user',
      content: question,
      time: new Date()
    }]);
    
    // Clear input
    setInputValue('');
    
    // Show thinking/typing indicator
    setIsAnswering(true);
    
    try {
      // Configure payload for the API request
      const payload: any = {
        recording_id: analysisId,
        question: question,
        provider: "openai",
        model: "gpt-4o"
      };
      
      const response = await axios.post('/api/textprocessing/image-qa', payload);
      
      const qaResponse: QAResponse = response.data.response;
      
      // Format the response message
      let answerText = qaResponse.answer;
      
      if (qaResponse.additional_information) {
        answerText += `\n\n**Additional Information**: ${qaResponse.additional_information}`;
      }
      
      if (qaResponse.references && qaResponse.references.length > 0) {
        answerText += `\n\n**References**: ${qaResponse.references.join(', ')}`;
      }
      
      // Add system response
      setMessages(prev => [...prev, {
        type: 'system',
        content: answerText,
        time: new Date()
      }]);
      
    } catch (err) {
      console.error("Error getting answer:", err);
      
      // Add error message
      setMessages(prev => [...prev, {
        type: 'system',
        content: "I'm sorry, I couldn't analyze that. Please try asking another question.",
        time: new Date()
      }]);
    } finally {
      setIsAnswering(false);
    }
  };

  if (!analysisId) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <Camera className="h-16 w-16 text-gray-300 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 mb-2">
          No Image Analysis Selected
        </h2>
        <p className="text-gray-500 max-w-md">
          Select an image analysis from the list to view details and ask questions.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700 mb-4"></div>
        <p className="text-gray-600">Loading image analysis...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-red-700 mb-2">Error</h2>
        <p className="text-gray-700">{error}</p>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <AlertCircle className="h-12 w-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 mb-2">No Data Available</h2>
        <p className="text-gray-600">There is no analysis data for this image.</p>
      </div>
    );
  }

  // Check if we have a simple upload record without analysis data
  const isSimpleUpload = !analysis.analysis;

  // Render old UI when not in Q&A mode
  if (!isQAMode) {
    return (
      <div className="flex-1 h-full overflow-auto bg-gray-50 relative">
        {/* Chat Sidebar - still accessible in old UI */}
        <div 
          ref={chatSidebarRef}
          className={`fixed top-0 right-0 h-full w-96 bg-white shadow-lg border-l border-gray-200 transition-transform duration-300 transform z-50 ${
            showChat ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full">
            <div className="p-4 bg-blue-50 border-b border-gray-200 flex justify-between items-center">
              <h2 className="flex items-center text-lg font-semibold text-gray-900">
                <HelpCircle className="h-5 w-5 text-blue-500 mr-2" />
                Image Q&A
              </h2>
              <button 
                onClick={() => setShowChat(false)}
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, idx) => (
                <div 
                  key={idx} 
                  className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div 
                    className={`rounded-lg p-3 max-w-[90%] ${
                      msg.type === 'user' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    <div 
                      className="text-sm" 
                      dangerouslySetInnerHTML={{ __html: parseMarkdown(msg.content) }}
                    />
                    <div className={`text-xs mt-1 ${msg.type === 'user' ? 'text-blue-200' : 'text-gray-500'}`}>
                      {format(msg.time, 'h:mm a')}
                    </div>
                  </div>
                </div>
              ))}
              {isAnswering && (
                <div className="flex justify-start">
                  <div className="rounded-lg p-3 bg-gray-100 text-gray-800">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            
            <form onSubmit={handleAskQuestion} className="border-t border-gray-200 p-4">
              <div className="flex">
                <input
                  type="text"
                  placeholder="Ask a question about this image..."
                  className="flex-1 border border-gray-300 rounded-l-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  disabled={isAnswering}
                />
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-r-lg px-4 py-2 flex items-center justify-center disabled:bg-blue-400"
                  disabled={!inputValue.trim() || isAnswering}
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Main content - Original UI */}
        <div className="max-w-5xl mx-auto p-6">
          {/* Header */}
          <div className="mb-6 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {analysis.analysis?.title || analysis.title || analysis.image_name || "Image Analysis"}
              </h1>
              <p className="text-gray-600">{formatDate(analysis.date || analysis.created_at || "")}</p>
            </div>
            <button 
              onClick={enterQAMode}
              className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
            >
              <HelpCircle className="h-4 w-4 mr-2" />
              Ask AI About Image
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Image */}
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <h2 className="flex items-center text-lg font-semibold text-gray-900 mb-4">
                <Camera className="h-5 w-5 text-blue-500 mr-2" />
                Image
              </h2>
              <div className="relative w-full h-64 bg-gray-100 rounded-lg overflow-hidden">
                {analysis.image_url ? (
                  <img
                    ref={imageRef}
                    src={analysis.image_url}
                    alt={analysis.image_name || analysis.title || "Medical image"}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full">
                    <Camera className="h-12 w-12 text-gray-400 mb-2" />
                    <p className="text-gray-500">Image not available</p>
                  </div>
                )}
              </div>
              <p className="mt-3 text-sm text-gray-600">{analysis.image_name || analysis.filename || ""}</p>
            </div>

            {/* Analysis Overview */}
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <h2 className="flex items-center text-lg font-semibold text-gray-900 mb-4">
                <FileText className="h-5 w-5 text-blue-500 mr-2" />
                Analysis Overview
              </h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Overall Impression</h3>
                  <p className="mt-1 text-sm text-gray-900">{analysis.analysis?.overall_impression || "Not available"}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Comparison</h3>
                  <p className="mt-1 text-sm text-gray-900">{analysis.analysis?.comparison || "Not available"}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Conclusion</h3>
                  <p className="mt-1 text-sm text-gray-900">{analysis.analysis?.conclusion || "Not available"}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Status</h3>
                  <div className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      analysis.status.toLowerCase() === "completed"
                        ? "bg-green-100 text-green-800"
                        : analysis.status.toLowerCase() === "failed" || analysis.status.toLowerCase().includes("failed")
                        ? "bg-red-100 text-red-800"
                        : "bg-blue-100 text-blue-800"
                    }`}>
                      {analysis.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Findings */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 mb-6">
            <h2 className="flex items-center text-lg font-semibold text-gray-900 mb-4">
              <ClipboardList className="h-5 w-5 text-blue-500 mr-2" />
              Findings
            </h2>
            
            {!analysis.analysis?.findings || analysis.analysis.findings.length === 0 ? (
              <p className="text-gray-600">No findings reported.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {analysis.analysis.findings.map((finding, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <Activity className="h-5 w-5 text-blue-500" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-gray-900">{finding.description}</h3>
                        <div className="mt-2 space-y-2">
                          <div>
                            <span className="text-xs font-medium text-gray-500">Location:</span>
                            <span className="ml-2 text-xs text-gray-900">{finding.location}</span>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-gray-500">Severity:</span>
                            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${formatSeverity(finding.severity)}`}>
                              {finding.severity}
                            </span>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-gray-500">Confidence:</span>
                            <span className="ml-2 text-xs text-gray-900">{finding.diagnostic_confidence}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <h2 className="flex items-center text-lg font-semibold text-gray-900 mb-4">
              <FileText className="h-5 w-5 text-blue-500 mr-2" />
              Metadata
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <h3 className="text-xs font-medium text-gray-500">Recording ID</h3>
                <p className="mt-1 text-sm text-gray-900">{analysis.recording_id}</p>
              </div>
              {analysis.transaction_id && (
                <div>
                  <h3 className="text-xs font-medium text-gray-500">Transaction ID</h3>
                  <p className="mt-1 text-sm text-gray-900">{analysis.transaction_id}</p>
                </div>
              )}
              {analysis.processing_type && (
                <div>
                  <h3 className="text-xs font-medium text-gray-500">Processing Type</h3>
                  <p className="mt-1 text-sm text-gray-900">{analysis.processing_type}</p>
                </div>
              )}
              <div>
                <h3 className="text-xs font-medium text-gray-500">Document Type</h3>
                <p className="mt-1 text-sm text-gray-900">{analysis.document_type}</p>
              </div>
              {analysis.metadata?.analysis_type && (
                <div>
                  <h3 className="text-xs font-medium text-gray-500">Analysis Type</h3>
                  <p className="mt-1 text-sm text-gray-900">{analysis.metadata.analysis_type}</p>
                </div>
              )}
              {analysis.metadata?.image_analysis_version && (
                <div>
                  <h3 className="text-xs font-medium text-gray-500">Version</h3>
                  <p className="mt-1 text-sm text-gray-900">{analysis.metadata.image_analysis_version}</p>
                </div>
              )}
              {analysis.source && (
                <div>
                  <h3 className="text-xs font-medium text-gray-500">Source</h3>
                  <p className="mt-1 text-sm text-gray-900">{analysis.source}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Overlay when chat is open */}
        {showChat && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-25 z-40 lg:hidden"
            onClick={() => setShowChat(false)}
          ></div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-1 h-full relative overflow-hidden bg-gray-50">
      {/* Main content area */}
      <div className={`flex-1 overflow-auto transition-all duration-300 ${showChat ? 'mr-96' : 'mr-0'}`}>
        {/* Fixed header with minimal controls - Microsoft-style command bar */}
        <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-6 py-3 shadow-sm">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">{analysis.title || "Image Analysis"}</h1>
            </div>
            <button
              onClick={exitQAMode}
              className="text-gray-700 hover:bg-gray-100 flex items-center text-sm font-medium px-3 py-1.5 rounded-sm transition-colors"
            >
              <X size={16} className="mr-1.5" />
              Exit
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {/* Image with highlighting capabilities */}
          <div className="max-w-4xl mx-auto mb-8">
            {/* Microsoft-style card with proper shadows */}
            <div className="bg-white rounded-lg overflow-hidden border border-gray-200 shadow">
              {/* Image container */}
              <div className="relative">
                <img 
                  ref={imageRef}
                  src={analysis.image_url} 
                  alt={analysis.title || "Medical image"} 
                  className="w-full h-auto max-h-[70vh] object-contain bg-[#f5f5f5]" 
                />
              </div>
              
              {/* Chat toggle button */}
              <div className="px-3 py-2 bg-white flex justify-end border-t border-gray-200">
                <button 
                  onClick={toggleChat}
                  className={`flex items-center px-3 py-1.5 rounded transition-colors ${
                    showChat 
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                      : 'bg-[#0078d4] text-white hover:bg-[#106ebe]'
                  }`}
                >
                  {showChat ? (
                    <>
                      <X size={16} className="mr-1.5" />
                      <span className="text-xs font-medium">Hide Q&A</span>
                    </>
                  ) : (
                    <>
                      <HelpCircle size={16} className="mr-1.5" />
                      <span className="text-xs font-medium">Ask Questions</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Analysis details - using Microsoft card patterns */}
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-white rounded-lg shadow border border-gray-200">
              <div className="px-6 py-3 border-b border-gray-200">
                <h2 className="text-base font-semibold text-gray-900">Analysis Summary</h2>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Date Analyzed</h3>
                    <p className="text-sm text-gray-900">{formatDate(analysis.analysis_date || analysis.date || analysis.created_at || "")}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Image Type</h3>
                    <p className="text-sm text-gray-900">{analysis.image_type || "Not specified"}</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Description</h3>
                  <p className="text-sm text-gray-900">{analysis.description || analysis.analysis?.overall_impression || "No description available"}</p>
                </div>
              </div>
            </div>
            
            {/* Findings panel - following Microsoft's data visualization patterns */}
            {(analysis.findings && analysis.findings.length > 0) || (analysis.analysis?.findings && analysis.analysis.findings.length > 0) ? (
              <div className="bg-white rounded-lg shadow border border-gray-200">
                <div className="px-6 py-3 border-b border-gray-200">
                  <h2 className="text-base font-semibold text-gray-900">Findings</h2>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {(analysis.findings || analysis.analysis?.findings || []).map((finding, index) => (
                      <div key={index} className="p-4 border border-gray-200 rounded bg-white hover:border-gray-300 transition-colors">
                        <h3 className="font-medium mb-2 text-sm">{finding.description}</h3>
                        
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          {finding.location && (
                            <div>
                              <span className="font-medium text-gray-500 uppercase tracking-wide">Location: </span>
                              <span className="text-gray-900">{finding.location}</span>
                            </div>
                          )}
                          
                          {finding.severity && (
                            <div>
                              <span className="font-medium text-gray-500 uppercase tracking-wide">Severity: </span>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${formatSeverity(finding.severity)}`}>
                                {finding.severity}
                              </span>
                            </div>
                          )}
                          
                          {(finding.confidence || finding.diagnostic_confidence) && (
                            <div>
                              <span className="font-medium text-gray-500 uppercase tracking-wide">Confidence: </span>
                              <span className="text-gray-900">{finding.confidence || finding.diagnostic_confidence}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      
      {/* Chat panel - styled following Microsoft's conversational UI patterns */}
      <div 
        ref={chatSidebarRef}
        className={`absolute top-0 bottom-0 right-0 w-96 bg-white border-l border-gray-200 shadow-lg transform transition-transform duration-300 ease-in-out ${
          showChat ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="bg-[#f0f6ff] px-4 py-3 border-b border-gray-200">
            <h2 className="font-medium flex items-center text-[#0078d4]">
              <HelpCircle size={16} className="mr-2" />
              Ask Questions About This Image
            </h2>
          </div>
          
          {/* Messages container - Microsoft conversational UI style */}
          <div className="flex-1 overflow-y-auto py-4 px-4 space-y-3 bg-[#f9f9f9]">
            {messages.length === 1 && (
              <div className="text-center p-6 bg-white rounded-lg border border-gray-200 shadow-sm my-8 mx-2">
                <div className="text-[#0078d4] mb-3">
                  <HelpCircle size={32} className="mx-auto" />
                </div>
                <p className="text-sm text-gray-700 mb-1 font-medium">
                  Ask questions about this image
                </p>
                <p className="text-xs text-gray-500">
                  Use natural language to inquire about the image
                </p>
              </div>
            )}
            
            {messages.map((message, index) => {
              // Skip the initial system message
              if (index === 0 && message.type === 'system') return null;
              
              return (
                <div 
                  key={index} 
                  className={`flex ${
                    message.type === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div 
                    className={`max-w-[85%] rounded-lg px-3 py-2.5 shadow-sm ${
                      message.type === 'user' 
                        ? 'bg-[#0078d4] text-white'
                        : 'bg-white text-gray-800 border border-gray-200'
                    }`}
                  >
                    <div 
                      className="text-sm"
                      dangerouslySetInnerHTML={{ 
                        __html: message.type === 'system' 
                          ? parseMarkdown(message.content) 
                          : message.content 
                      }}
                    />
                  </div>
                </div>
              );
            })}
            
            {isAnswering && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 rounded-lg px-3 py-2.5 shadow-sm border border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className="flex space-x-1">
                      <div className="h-2 w-2 bg-[#0078d4] rounded-full animate-pulse"></div>
                      <div className="h-2 w-2 bg-[#0078d4] rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></div>
                      <div className="h-2 w-2 bg-[#0078d4] rounded-full animate-pulse" style={{ animationDelay: '600ms' }}></div>
                    </div>
                    <span className="text-xs text-gray-500">Analyzing image...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Input area - Microsoft Search box style */}
          <div className="p-3 border-t border-gray-200 bg-white">
            <form onSubmit={handleAskQuestion} className="flex items-center">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Ask about this image..."
                  className="w-full border border-gray-300 rounded px-3 py-2 pr-10 focus:outline-none focus:border-[#0078d4] focus:ring-1 focus:ring-[#0078d4] text-sm"
                  disabled={isAnswering}
                />
                <button
                  type="submit"
                  disabled={!inputValue.trim() || isAnswering}
                  className={`absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded-full ${
                    !inputValue.trim() || isAnswering
                      ? 'text-gray-400'
                      : 'text-[#0078d4] hover:bg-[#f0f6ff]'
                  }`}
                  aria-label="Send message"
                >
                  <Send size={16} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}; 